/**
 * API-Client für den JARVIS HTTP-Server. Same-origin in Produktion, via Vite-Proxy in dev.
 */

const TOKEN_KEY = "jarvis.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function login(email: string, password: string): Promise<string> {
  const res = await fetch("/auth", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  if (!data.token) throw new Error("server returned no token");
  return data.token;
}

export interface ChatEvents {
  onThinking?(): void;
  onChunk(text: string): void;
  onDone(fullText: string): void;
  onError(message: string): void;
}

export async function streamChat(
  text: string,
  signal: AbortSignal,
  events: ChatEvents,
): Promise<void> {
  const res = await fetch("/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ text }),
    signal,
  });
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    events.onError(`HTTP ${res.status}: ${body.slice(0, 200)}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE-Frames sind durch "\n\n" getrennt.
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const ev = parseSseFrame(frame);
      if (!ev) continue;
      if (ev.event === "thinking") events.onThinking?.();
      else if (ev.event === "chunk") {
        const piece = (ev.data?.text as string) ?? "";
        if (piece) events.onChunk(piece);
      } else if (ev.event === "done") {
        events.onDone((ev.data?.text as string) ?? "");
        return;
      } else if (ev.event === "error") {
        events.onError((ev.data?.message as string) ?? "unknown error");
        return;
      }
    }
  }
}

function parseSseFrame(frame: string): { event: string; data: any } | null {
  const lines = frame.split("\n");
  let event = "message";
  let data = "";
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!data) return { event, data: {} };
  try {
    return { event, data: JSON.parse(data) };
  } catch {
    return { event, data: { text: data } };
  }
}

export async function transcribeAudio(blob: Blob, filename = "input.webm"): Promise<string> {
  const form = new FormData();
  form.append("audio", blob, filename);
  const res = await fetch("/stt", {
    method: "POST",
    headers: { ...authHeader() },
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return (data.text || "").trim();
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch("/account/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
}

export async function synthesize(text: string, voice?: string): Promise<Blob> {
  const res = await fetch("/tts", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(voice ? { text, voice } : { text }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return await res.blob();
}

export interface SessionFile {
  name: string;
  mtime: string;
  size: number;
  date: string;
  time: string;
  label: string;
  title: string;
}

export async function listSessions(): Promise<SessionFile[]> {
  const res = await fetch("/sessions", { headers: { ...authHeader() } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data.files) ? (data.files as SessionFile[]) : [];
}

export async function fetchSessionMarkdown(name: string): Promise<string> {
  const res = await fetch(`/sessions/${encodeURIComponent(name)}`, { headers: { ...authHeader() } });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(body.slice(0, 200) || `HTTP ${res.status}`);
  }
  return await res.text();
}

// ── Image-Upload ──────────────────────────────────────────────────────────
// Two flows:
//   1) uploadImage(file, onProgress) — single-shot multipart POST /upload.
//      XHR is used (not fetch) damit wir upload-byte-Progress haben (fetch hat
//      keinen onUploadProgress).
//   2) pollUploadProgress(id) — fragt /upload/:id/progress; im Backend wechselt
//      der Status nach `done` sobald das Thumbnail generiert ist.

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadStartResponse {
  id: string;
  status: string;
  progressUrl: string;
  fileUrl: string;
  thumbUrl: string;
}

export function uploadImage(
  file: File | Blob,
  filename: string,
  onProgress?: (ev: UploadProgressEvent) => void,
  signal?: AbortSignal,
): Promise<UploadStartResponse> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("image", file, filename);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/upload");
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      const total = e.total || file.size || 0;
      const percent = total > 0 ? Math.round((e.loaded / total) * 100) : 0;
      onProgress({ loaded: e.loaded, total, percent });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error("server returned invalid JSON"));
        }
      } else {
        let msg = `HTTP ${xhr.status}`;
        try {
          const j = JSON.parse(xhr.responseText);
          if (j && j.error) msg = j.error;
        } catch {
          // ignore
        }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.onabort = () => reject(new Error("aborted"));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        reject(new Error("aborted"));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

export interface UploadProgress {
  id: string;
  status: "pending" | "uploading" | "processing" | "done" | "failed";
  progress: number;
  bytes_received: number;
  size: number;
  mime: string;
  filename: string;
  thumbAvailable: boolean;
  createdAt: string;
  finishedAt: string | null;
  error: string | null;
  fileUrl: string;
  thumbUrl: string;
  progressUrl: string;
  sha256?: string | null;
}

export async function fetchUploadProgress(id: string): Promise<UploadProgress> {
  const res = await fetch(`/upload/${encodeURIComponent(id)}/progress`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return (await res.json()) as UploadProgress;
}

export async function listUploads(): Promise<UploadProgress[]> {
  const res = await fetch("/uploads", { headers: { ...authHeader() } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data.uploads) ? (data.uploads as UploadProgress[]) : [];
}

export async function deleteUpload(id: string): Promise<void> {
  const res = await fetch(`/uploads/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
}

/** Liefert eine same-origin Thumb-URL inkl. Bearer-Token als Query-Param? Nein —
 *  Token darf NICHT in URL. Wir benutzen blob-URLs nach authentifiziertem fetch. */
export async function fetchThumbBlobUrl(id: string): Promise<string> {
  const res = await fetch(`/uploads/${encodeURIComponent(id)}/thumb`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
